import type { SessionId } from '../../shared/types/ids';
import type { GameSessionSummary, SessionRecord } from '../../shared/types/session';
import type { SessionRepository } from '../interfaces/repositories';
import { projectSessions, projectSessionSummaries } from './governed-records';
import type { JsonStore } from './json-store';
import { STORAGE_KEYS } from './keys';
import { toMissionProgressDto, toSessionRecordDto, toSessionSummaryDto } from './persistence-dto';

/** Keeps local storage bounded — a family does not need an unlimited session history. */
const MAX_RETAINED_SESSIONS = 50;

type SummaryTable = Record<SessionId, GameSessionSummary>;

/**
 * Sessions record what happened, never who was faster and never any camera, landmark or audio
 * trace. The DTO projection is what makes that true at runtime rather than by convention.
 */
export function createLocalSessionRepository(store: JsonStore): SessionRepository {
  const readSessions = (): SessionRecord[] =>
    store.read(STORAGE_KEYS.sessions, projectSessions) ?? [];

  /**
   * The bounded history, **minus the row a live sitting is anchored to** (P6R16-3).
   *
   * A mission checkpoint is predicated on its own session row still being here (the erasure anchor
   * map), and the history is capped — so a family with a long evening behind them could age out the
   * row of the sitting they are *still playing*, and its next checkpoint would be refused silently.
   * A child would come back to a mission that had forgotten where they were, with storage reporting
   * itself perfectly healthy.
   *
   * The protected set is **derived from the store**, not tracked: there is exactly one in-flight
   * snapshot slot, and the session it names is the sitting that is still going. No new key, no
   * bookkeeping to drift, and the fifty-row promise stands for everything else — a row nothing
   * points at ages out exactly as before.
   */
  const retain = (sessions: SessionRecord[]): SessionRecord[] => {
    if (sessions.length <= MAX_RETAINED_SESSIONS) return sessions;
    const anchored = store.read(STORAGE_KEYS.missionProgress, toMissionProgressDto)?.sessionId;
    const kept = sessions.slice(0, MAX_RETAINED_SESSIONS);
    if (anchored === undefined || kept.some((entry) => entry.sessionId === anchored)) return kept;

    const active = sessions.find((entry) => entry.sessionId === anchored);
    // The in-flight sitting takes the last place rather than an extra one: the cap is a promise
    // about how much of a family's history is kept, and it stays exactly as wide as it was.
    return active === undefined ? kept : [...kept.slice(0, MAX_RETAINED_SESSIONS - 1), active];
  };

  const readSummaries = (): SummaryTable =>
    store.read(STORAGE_KEYS.sessionSummaries, projectSessionSummaries) ?? {};

  return {
    async startSession(session: SessionRecord): Promise<void> {
      const projected = toSessionRecordDto(session);
      if (projected === null) return;

      const sessions = readSessions().filter((entry) => entry.sessionId !== projected.sessionId);
      sessions.unshift(projected);
      store.write(STORAGE_KEYS.sessions, retain(sessions));
    },

    async completeSession(sessionId: SessionId, summary: GameSessionSummary): Promise<void> {
      const projected = toSessionSummaryDto(summary);
      if (projected === null) return;

      const existing = readSessions();
      /**
       * A summary for a session that is not here belongs nowhere (P6R16-4).
       *
       * The two keys are kept consistent below by pruning summaries against the live session set —
       * so completing a session that has gone leaves an empty `sessions` array and an empty
       * `session-summaries` object, written for nothing. After a parent's delete that is *residue*:
       * two governed keys back on the disk, holding nothing, in a namespace they were told was
       * empty. Nothing to render, and nothing that should be there.
       */
      if (!existing.some((entry) => entry.sessionId === sessionId)) return;

      const sessions = existing.map((entry) =>
        entry.sessionId === sessionId ? { ...entry, completedAt: projected.completedAt } : entry,
      );
      store.write(STORAGE_KEYS.sessions, sessions);

      const summaries = readSummaries();
      summaries[sessionId] = projected;

      // Drop summaries whose session has aged out, so the two keys cannot drift apart.
      const liveIds = new Set(sessions.map((entry) => entry.sessionId));
      for (const key of Object.keys(summaries)) {
        if (!liveIds.has(key)) delete summaries[key];
      }
      store.write(STORAGE_KEYS.sessionSummaries, summaries);
    },

    async listRecentSessions(limit: number): Promise<SessionRecord[]> {
      return readSessions().slice(0, Math.max(0, limit));
    },

    async getSummary(sessionId: SessionId): Promise<GameSessionSummary | null> {
      return readSummaries()[sessionId] ?? null;
    },
  };
}
